import React, { useRef, useState, useMemo } from 'react';
import { Canvas, useFrame, ThreeEvent } from '@react-three/fiber';
import { OrbitControls, Text, Html, Stars } from '@react-three/drei';
import * as THREE from 'three';
import { KinshipRecord } from '@/lib/mock-data';
import { Permutation, PedigreeNode } from '@/lib/permutation-engine';
import { ChevronLeft, ChevronRight } from 'lucide-react';

interface Individual {
  id: string;
  name: string;
  gender: string;
  position: [number, number, number];
  isUntested?: boolean;
}

interface Relationship {
  from: string;
  to: string;
  type: 'PC' | 'FS' | 'EXT';
  lr: number;
}

interface NodeProps {
  individual: Individual;
  isHovered: boolean;
  onHover: (id: string | null) => void;
}

function IndividualNode({ individual, isHovered, onHover }: NodeProps) {
  const meshRef = useRef<THREE.Mesh>(null);
  const [hovered, setHovered] = useState(false);
  
  useFrame((state) => {
    if (meshRef.current) {
      meshRef.current.rotation.y += 0.005;
      const scale = hovered || isHovered ? 1.3 : 1;
      meshRef.current.scale.lerp(new THREE.Vector3(scale, scale, scale), 0.1);
    }
  });

  const isUntested = individual.isUntested;
  const baseColor = individual.gender === 'M' ? '#3b82f6' : individual.gender === 'F' ? '#ec4899' : '#6b7280';
  const baseEmissive = individual.gender === 'M' ? '#1d4ed8' : individual.gender === 'F' ? '#be185d' : '#374151';
  const color = isUntested ? '#4b5563' : baseColor;
  const emissiveColor = isUntested ? '#1f2937' : baseEmissive;

  return (
    <group position={individual.position}>
      <mesh
        ref={meshRef}
        onPointerOver={(e: ThreeEvent<PointerEvent>) => { e.stopPropagation(); setHovered(true); onHover(individual.id); }}
        onPointerOut={() => { setHovered(false); onHover(null); }}
      >
        <sphereGeometry args={[isUntested ? 0.3 : 0.4, 32, 32]} />
        <meshStandardMaterial
          color={color}
          emissive={emissiveColor}
          emissiveIntensity={hovered || isHovered ? 0.5 : 0.2}
          metalness={0.3}
          roughness={0.4}
          transparent={isUntested}
          opacity={isUntested ? 0.7 : 1}
        />
      </mesh>
      <Text
        position={[0, 0.7, 0]}
        fontSize={0.25}
        color={isUntested ? '#9ca3af' : 'white'}
        anchorX="center"
        anchorY="middle"
        outlineWidth={0.02}
        outlineColor="#000000"
      >
        {individual.name.split(' ')[0]}
      </Text>
      {(hovered || isHovered) && (
        <Html position={[0, 1.2, 0]} center>
          <div className="bg-slate-900/90 text-white px-3 py-2 rounded-lg shadow-xl text-xs whitespace-nowrap" data-testid={`tooltip-${individual.id}`}>
            <div className="font-semibold">{individual.name}</div>
            <div className="text-slate-300">
              {individual.gender === 'M' ? 'Male' : individual.gender === 'F' ? 'Female' : 'Unknown'}
              {isUntested && <span className="ml-1 text-amber-400">(Untested)</span>}
            </div>
          </div>
        </Html>
      )}
    </group>
  );
}

interface EdgeProps {
  from: [number, number, number];
  to: [number, number, number];
  type: 'PC' | 'FS' | 'EXT';
  lr: number;
  isHighlighted: boolean;
}

function RelationshipEdge({ from, to, type, isHighlighted }: EdgeProps) {
  const color = type === 'PC' ? '#22c55e' : type === 'FS' ? '#f59e0b' : '#8b5cf6';
  const opacity = isHighlighted ? 1 : 0.6;

  const points = useMemo(() => {
    const start = new THREE.Vector3(...from);
    const end = new THREE.Vector3(...to);
    const mid = new THREE.Vector3().lerpVectors(start, end, 0.5);
    mid.y += 0.3;
    const curve = new THREE.QuadraticBezierCurve3(start, mid, end);
    return curve.getPoints(20);
  }, [from, to]);

  const geometry = useMemo(() => {
    return new THREE.BufferGeometry().setFromPoints(points);
  }, [points]);

  return (
    <primitive object={new THREE.Line(geometry, new THREE.LineBasicMaterial({ color, transparent: true, opacity }))} />
  );
}

function Scene({ individuals, relationships, hoveredId, onHover }: {
  individuals: Individual[];
  relationships: Relationship[];
  hoveredId: string | null;
  onHover: (id: string | null) => void;
}) {
  const individualMap = useMemo(() => {
    const map = new Map<string, Individual>();
    individuals.forEach(ind => map.set(ind.id, ind));
    return map;
  }, [individuals]);

  const highlightedEdges = useMemo(() => {
    if (!hoveredId) return new Set<string>();
    const edges = new Set<string>();
    relationships.forEach(rel => {
      if (rel.from === hoveredId || rel.to === hoveredId) {
        edges.add(`${rel.from}-${rel.to}`);
      }
    });
    return edges;
  }, [hoveredId, relationships]);

  return (
    <>
      <ambientLight intensity={0.4} />
      <pointLight position={[10, 10, 10]} intensity={1} />
      <pointLight position={[-10, -10, -10]} intensity={0.5} color="#6366f1" />
      <Stars radius={100} depth={50} count={1000} factor={4} saturation={0} fade speed={1} />
      
      {relationships.map((rel, i) => {
        const fromInd = individualMap.get(rel.from);
        const toInd = individualMap.get(rel.to);
        if (!fromInd || !toInd) return null;
        const edgeKey = `${rel.from}-${rel.to}`;
        return (
          <RelationshipEdge
            key={i}
            from={fromInd.position}
            to={toInd.position}
            type={rel.type}
            lr={rel.lr}
            isHighlighted={highlightedEdges.has(edgeKey)}
          />
        );
      })}
      
      {individuals.map((ind) => (
        <IndividualNode
          key={ind.id}
          individual={ind}
          isHovered={hoveredId === ind.id || (hoveredId !== null && relationships.some(r => 
            (r.from === hoveredId && r.to === ind.id) || (r.to === hoveredId && r.from === ind.id)
          ))}
          onHover={onHover}
        />
      ))}
      
      <OrbitControls 
        enablePan={true}
        enableZoom={true}
        enableRotate={true}
        autoRotate={false}
        minDistance={3}
        maxDistance={20}
      />
    </>
  );
}

interface Kinship3DViewProps {
  kinshipRecords: KinshipRecord[];
  categoryFilter?: 'all' | 'parentChild' | 'fullSibling' | 'extended';
  permutations?: Permutation[];
  currentIndex?: number;
  onPrev?: () => void;
  onNext?: () => void;
}

function convertPermutationToScene(permutation: Permutation): { individuals: Individual[]; relationships: Relationship[] } {
  const individuals: Individual[] = [];
  const relationships: Relationship[] = [];
  const allNodes = [...permutation.nodes, ...(permutation.nodesB || [])];
  
  const n = allNodes.length;
  const radius = Math.max(3, n * 0.7);
  
  allNodes.forEach((node, i) => {
    const angle = (i / n) * Math.PI * 2;
    const yOffset = node.data.isUntested ? -1 : (node.parents.length > 0 ? -0.5 : 0.5);
    
    individuals.push({
      id: node.id,
      name: node.data.name,
      gender: node.gender === 'male' ? 'M' : 'F',
      position: [
        Math.cos(angle) * radius,
        yOffset,
        Math.sin(angle) * radius
      ],
      isUntested: node.data.isUntested
    });
    
    node.children.forEach(child => {
      if (!relationships.some(r => 
        (r.from === node.id && r.to === child.id) || 
        (r.from === child.id && r.to === node.id)
      )) {
        relationships.push({
          from: node.id,
          to: child.id,
          type: 'PC',
          lr: 1
        });
      }
    });
    
    node.siblings.forEach(sibling => {
      if (!relationships.some(r => 
        (r.from === node.id && r.to === sibling.id) || 
        (r.from === sibling.id && r.to === node.id)
      )) {
        relationships.push({
          from: node.id,
          to: sibling.id,
          type: 'FS',
          lr: 1
        });
      }
    });
    
    node.spouses.forEach(spouse => {
      if (!relationships.some(r => 
        (r.from === node.id && r.to === spouse.id) || 
        (r.from === spouse.id && r.to === node.id)
      )) {
        relationships.push({
          from: node.id,
          to: spouse.id,
          type: 'EXT',
          lr: 1
        });
      }
    });
  });
  
  return { individuals, relationships };
}

export function Kinship3DView({ 
  kinshipRecords, 
  categoryFilter = 'all',
  permutations = [],
  currentIndex = 0,
  onPrev,
  onNext
}: Kinship3DViewProps) {
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  
  const hasPermutations = permutations.length > 0;
  const currentPermutation = hasPermutations ? permutations[Math.min(currentIndex, permutations.length - 1)] : null;

  const { individuals, relationships, filteredRelationships } = useMemo(() => {
    if (currentPermutation) {
      const { individuals, relationships } = convertPermutationToScene(currentPermutation);
      return { individuals, relationships, filteredRelationships: relationships };
    }
    
    const indMap = new Map<string, Individual>();
    const rels: Relationship[] = [];
    
    kinshipRecords.forEach((record) => {
      if (!indMap.has(record.relative1.id)) {
        indMap.set(record.relative1.id, {
          id: record.relative1.id,
          name: record.relative1.name,
          gender: record.relative1.gender,
          position: [0, 0, 0]
        });
      }
      if (!indMap.has(record.relative2.id)) {
        indMap.set(record.relative2.id, {
          id: record.relative2.id,
          name: record.relative2.name,
          gender: record.relative2.gender,
          position: [0, 0, 0]
        });
      }
      
      const maxLR = Math.max(record.parentChildLR, record.fullSiblingLR, record.extendedFamilyLR);
      let type: 'PC' | 'FS' | 'EXT' = 'EXT';
      let lr = record.extendedFamilyLR;
      
      if (record.parentChildLR === maxLR && record.parentChildLR > 1) {
        type = 'PC';
        lr = record.parentChildLR;
      } else if (record.fullSiblingLR === maxLR && record.fullSiblingLR > 1) {
        type = 'FS';
        lr = record.fullSiblingLR;
      }
      
      if (maxLR > 1) {
        rels.push({
          from: record.relative1.id,
          to: record.relative2.id,
          type,
          lr
        });
      }
    });
    
    const individuals = Array.from(indMap.values());
    const n = individuals.length;
    const radius = Math.max(2, n * 0.8);
    
    individuals.forEach((ind, i) => {
      const angle = (i / n) * Math.PI * 2;
      const yOffset = (Math.random() - 0.5) * 2;
      ind.position = [
        Math.cos(angle) * radius,
        yOffset,
        Math.sin(angle) * radius
      ];
    });
    
    const filteredRels = categoryFilter === 'all' 
      ? rels 
      : rels.filter(r => {
          if (categoryFilter === 'parentChild') return r.type === 'PC';
          if (categoryFilter === 'fullSibling') return r.type === 'FS';
          if (categoryFilter === 'extended') return r.type === 'EXT';
          return true;
        });
    
    return { individuals, relationships: rels, filteredRelationships: filteredRels };
  }, [kinshipRecords, categoryFilter, currentPermutation]);

  if (kinshipRecords.length === 0) {
    return (
      <div className="w-full h-[600px] flex items-center justify-center bg-slate-900 rounded-xl" data-testid="3d-empty-state">
        <div className="text-center text-slate-400">
          <div className="text-lg font-medium">No kinship data</div>
          <div className="text-sm">Select a case with kinship records to visualize</div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full h-[600px] relative rounded-xl overflow-hidden" data-testid="kinship-3d-container">
      <Canvas
        camera={{ position: [0, 5, 10], fov: 60 }}
        style={{ background: 'linear-gradient(to bottom, #0f172a, #1e1b4b)' }}
      >
        <Scene
          individuals={individuals}
          relationships={filteredRelationships}
          hoveredId={hoveredId}
          onHover={setHoveredId}
        />
      </Canvas>
      
      {/* Navigation Arrows */}
      {hasPermutations && permutations.length > 1 && (
        <>
          <button
            onClick={onPrev}
            className="absolute left-4 top-1/2 -translate-y-1/2 bg-slate-900/80 hover:bg-slate-800 backdrop-blur-sm rounded-full p-3 text-white transition-all hover:scale-110 shadow-lg border border-slate-700"
            data-testid="button-prev-permutation"
          >
            <ChevronLeft className="w-8 h-8" />
          </button>
          <button
            onClick={onNext}
            className="absolute right-4 top-1/2 -translate-y-1/2 bg-slate-900/80 hover:bg-slate-800 backdrop-blur-sm rounded-full p-3 text-white transition-all hover:scale-110 shadow-lg border border-slate-700"
            data-testid="button-next-permutation"
          >
            <ChevronRight className="w-8 h-8" />
          </button>
        </>
      )}
      
      {/* Permutation Counter */}
      {hasPermutations && (
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-slate-900/90 backdrop-blur-sm rounded-lg px-4 py-2 text-center" data-testid="permutation-counter">
          <div className="text-white font-semibold text-sm">
            Permutation {currentIndex + 1} of {permutations.length}
          </div>
          {currentPermutation && (
            <div className="text-slate-400 text-xs mt-0.5 max-w-xs truncate">
              {currentPermutation.label}
            </div>
          )}
        </div>
      )}
      
      <div className="absolute bottom-4 left-4 bg-slate-900/80 backdrop-blur-sm rounded-lg p-3 text-xs text-white" data-testid="legend-3d">
        <div className="font-semibold mb-2">Legend</div>
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-blue-500" />
            <span>Male</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-pink-500" />
            <span>Female</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-gray-500 opacity-70" />
            <span>Untested</span>
          </div>
          <div className="flex items-center gap-2 mt-2 pt-2 border-t border-slate-700">
            <div className="w-4 h-0.5 bg-green-500" />
            <span>Parent/Child</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-0.5 bg-amber-500" />
            <span>Full Sibling</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-0.5 bg-violet-500" />
            <span>Spouse</span>
          </div>
        </div>
      </div>
      
      <div className="absolute top-4 right-4 bg-slate-900/80 backdrop-blur-sm rounded-lg px-3 py-2 text-xs text-slate-300" data-testid="3d-controls-hint">
        <div>Drag to rotate • Scroll to zoom • Shift+drag to pan</div>
      </div>
      
      <div className="absolute top-4 left-4 bg-slate-900/80 backdrop-blur-sm rounded-lg px-3 py-2" data-testid="3d-stats">
        <div className="text-sm font-semibold text-white">{individuals.length} Individuals</div>
        <div className="text-xs text-slate-400">
          {filteredRelationships.length} Relationships
          {hasPermutations && currentPermutation && (
            <span className="ml-1 text-violet-400">
              ({currentPermutation.type})
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
